import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiZap, FiCamera, FiHome, FiSun, FiShield, FiBattery, FiWifi,
  FiX, FiExternalLink, FiGrid, FiPlay, FiThumbsUp, FiShare2
} = FiIcons;

// ─────────────────────────────────────────────────────────────────────────────
// HOW TO ADD YOUR FACEBOOK POSTS:
//
// 1. Go to your Facebook page: facebook.com/tayyabelectricstudio
// 2. Open any post or video
// 3. Click the 3 dots (...) on the post → click "Copy link"
// 4. Paste that link as the "postUrl" below
// 5. Change the title, category and description to match the post
// 
// EXAMPLE postUrl:
// "https://www.facebook.com/tayyabelectricstudio/posts/1234567890"
// "https://www.facebook.com/tayyabelectricstudio/videos/1234567890"
// ─────────────────────────────────────────────────────────────────────────────
const posts = [
  {
    id: 1,
    type: 'video',           // 'video' or 'post'
    postUrl: 'https://www.facebook.com/reel/782437024418731',
    title: 'CCTV Installation Project',
    location: 'Baramulla',
    category: 'cctv',
    icon: FiCamera,
    color: 'from-purple-600 to-pink-600',
  },
  {
    id: 2,
    type: 'video',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Solar Panel Installation',
    location: 'Sopore',
    category: 'solar',
    icon: FiSun,
    color: 'from-yellow-500 to-orange-500',
  },
  {
    id: 3,
    type: 'post',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Smart Home Automation Setup',
    location: 'Fatehgarh',
    category: 'smarthome',
    icon: FiHome,
    color: 'from-green-500 to-teal-500',
  },
  {
    id: 4,
    type: 'video',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Electrical Wiring Project',
    location: 'Baramulla',
    category: 'electrical',
    icon: FiZap,
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 5,
    type: 'post',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Security System Installation',
    location: 'Pattan',
    category: 'security',
    icon: FiShield,
    color: 'from-indigo-500 to-purple-500',
  },
  {
    id: 6,
    type: 'video',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Inverter & UPS Setup',
    location: 'Sopore',
    category: 'inverter',
    icon: FiBattery,
    color: 'from-red-500 to-orange-500',
  },
  {
    id: 7,
    type: 'post',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Networking & Cabling',
    location: 'Baramulla',
    category: 'networking',
    icon: FiWifi,
    color: 'from-cyan-500 to-blue-500',
  },
  {
    id: 8,
    type: 'video',
    postUrl: 'https://www.facebook.com/tayyabelectricstudio',
    title: 'Complete Home Wiring',
    location: 'Rafiabad',
    category: 'electrical',
    icon: FiZap,
    color: 'from-blue-600 to-indigo-500',
  },
];

const categories = [
  { id: 'all',        label: 'All',         icon: FiGrid    },
  { id: 'cctv',       label: 'CCTV',        icon: FiCamera  },
  { id: 'solar',      label: 'Solar',       icon: FiSun     },
  { id: 'electrical', label: 'Electrical',  icon: FiZap     },
  { id: 'smarthome',  label: 'Smart Home',  icon: FiHome    },
  { id: 'security',   label: 'Security',    icon: FiShield  },
  { id: 'networking', label: 'Networking',  icon: FiWifi    },
  { id: 'inverter',   label: 'Inverter',    icon: FiBattery },
];

// ── Facebook SDK loader ───────────────────────────────────────────────────────
const useFacebookSDK = () => {
  useEffect(() => {
    if (window.FB) { window.FB.XFBML.parse(); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ xfbml: true, version: 'v18.0' });
    };
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);
};

// ── Facebook Embed Modal ──────────────────────────────────────────────────────
const EmbedModal = ({ post, onClose }) => {
  useEffect(() => {
    if (window.FB) window.FB.XFBML.parse();
    const timer = setTimeout(() => { if (window.FB) window.FB.XFBML.parse(); }, 800);
    return () => clearTimeout(timer);
  }, [post]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.85, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85, y: 30 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose}
          className="absolute -top-12 right-0 text-gray-400 hover:text-white transition-colors z-10 flex items-center space-x-2">
          <span className="text-sm">Close</span>
          <SafeIcon icon={FiX} className="text-xl" />
        </button>

        {/* Header */}
        <div className="bg-gray-900 rounded-t-2xl px-5 py-4 border border-gray-700/50 border-b-0 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-9 h-9 bg-gradient-to-br ${post.color} rounded-lg flex items-center justify-center`}>
              <SafeIcon icon={post.icon} className="text-white text-sm" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{post.title}</p>
              <p className="text-gray-500 text-xs">📍 {post.location}</p>
            </div>
          </div>
          <a href={post.postUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-xs transition-colors">
            <span>Open</span>
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </a>
        </div>

        {/* Facebook Embed */}
        <div className="bg-white rounded-b-2xl overflow-hidden flex items-center justify-center min-h-[400px]">
          {post.type === 'video' ? (
            <div
              className="fb-video"
              data-href={post.postUrl}
              data-width="500"
              data-show-text="false"
              data-autoplay="true"
            />
          ) : (
            <div
              className="fb-post"
              data-href={post.postUrl}
              data-width="500"
              data-show-text="true"
            />
          )}
        </div>

        {/* Fallback link */}
        <div className="mt-3 text-center">
          <a href={post.postUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm transition-colors">
            <span>Not loading? View on Facebook</span>
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Post Card ─────────────────────────────────────────────────────────────────
const PostCard = ({ post, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.5, delay: index * 0.06 }}
    onClick={() => onClick(post)}
    className="group relative rounded-2xl overflow-hidden cursor-pointer border border-gray-800/60 hover:border-blue-500/40 transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl hover:shadow-blue-500/10"
    style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))' }}
  >
    {/* Thumbnail area */}
    <div className={`relative h-52 bg-gradient-to-br ${post.color} flex items-center justify-center overflow-hidden`}>
      {/* Pattern */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} />
      {/* Shimmer */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Icon */}
      <SafeIcon icon={post.icon} className="text-6xl text-white/25" />

      {/* Play button for videos */}
      {post.type === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/40 group-hover:scale-110 group-hover:bg-white/30 transition-all duration-300">
            <SafeIcon icon={FiPlay} className="text-white text-xl ml-1" />
          </div>
        </div>
      )}

      {/* Type badge */}
      <div className={`absolute top-3 left-3 flex items-center space-x-1.5 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold tracking-wider ${
        post.type === 'video' ? 'bg-blue-600/80 text-white' : 'bg-white/20 text-white'
      }`}>
        <SafeIcon icon={post.type === 'video' ? FiPlay : FiThumbsUp} className="text-xs" />
        <span>{post.type === 'video' ? 'VIDEO' : 'POST'}</span>
      </div>

      {/* Facebook badge */}
      <div className="absolute top-3 right-3 bg-[#1877F2]/80 backdrop-blur-sm rounded-full px-2 py-1">
        <span className="text-white text-[10px] font-black">f</span>
      </div>
    </div>

    {/* Info */}
    <div className="p-5">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-white font-bold text-sm leading-tight group-hover:text-blue-300 transition-colors duration-300">
          {post.title}
        </h3>
        <span className="text-gray-500 text-[10px] ml-2 flex-shrink-0">📍 {post.location}</span>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-gray-600 text-xs">Tayyab Electric Studio</span>
        <div className="flex items-center space-x-1 text-blue-400 text-xs group-hover:text-blue-300">
          <SafeIcon icon={FiExternalLink} className="text-xs" />
          <span>View</span>
        </div>
      </div>
    </div>

    {/* Bottom accent */}
    <div className={`absolute bottom-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-500 bg-gradient-to-r ${post.color}`} />
  </motion.div>
);

// ── Main Gallery ──────────────────────────────────────────────────────────────
const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activePost, setActivePost] = useState(null);

  useFacebookSDK();

  const filtered = activeCategory === 'all'
    ? posts
    : posts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen pt-16 md:pt-20">

      {/* Facebook SDK root div */}
      <div id="fb-root" />

      {/* ── Hero ── */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl animate-pulse" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300">
            <SafeIcon icon={FiGrid} className="text-lg" />
            <span>Our Work Portfolio</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold">
            <span className="gradient-text">Projects We're</span><br />
            <span className="text-white">Proud Of</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Browse our latest projects and videos from Facebook. Real work, real results across Kashmir.
          </p>

          {/* Stats row */}
          <div className="flex items-center justify-center space-x-6 pt-2">
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>{posts.length} Projects</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span>{posts.filter(p => p.type === 'video').length} Videos</span>
            </div>
            <a
              href="https://www.facebook.com/tayyabelectricstudio"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-[#1877F2]/20 border border-[#1877F2]/40 text-blue-300 rounded-full px-4 py-1.5 text-sm hover:bg-[#1877F2]/30 transition-colors"
            >
              <span className="font-black text-base leading-none">f</span>
              <span>Follow on Facebook</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── Category Filter ── */}
      <section className="py-6 bg-black sticky top-20 z-30 border-b border-gray-800/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/60 hover:text-white border border-gray-700/50'
                }`}
              >
                <SafeIcon icon={cat.icon} className="text-sm" />
                <span>{cat.label}</span>
                {activeCategory === cat.id && cat.id !== 'all' && (
                  <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {posts.filter(p => p.category === cat.id).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Grid ── */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex items-center justify-between mb-8">
            <p className="text-gray-400 text-sm">
              Showing <span className="text-white font-semibold">{filtered.length}</span> posts
              {activeCategory !== 'all' && (
                <span> in <span className="text-blue-400">{categories.find(c => c.id === activeCategory)?.label}</span></span>
              )}
            </p>
            <a
              href="https://www.facebook.com/tayyabelectricstudio"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-[#1877F2]/20 hover:bg-[#1877F2]/30 border border-[#1877F2]/40 text-blue-300 px-4 py-2 rounded-full text-sm transition-all duration-300"
            >
              <SafeIcon icon={FiShare2} className="text-sm" />
              <span>See All on Facebook</span>
            </a>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filtered.map((post, index) => (
                <PostCard key={post.id} post={post} index={index} onClick={setActivePost} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Facebook CTA ── */}
      <section className="py-16 bg-black">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-br from-[#1877F2]/10 to-gray-900/40 border border-[#1877F2]/30 rounded-2xl p-8">
            <div className="w-16 h-16 bg-[#1877F2]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-[#1877F2] text-4xl font-black">f</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Follow Us on Facebook</h3>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Stay updated with our latest projects, tips, and offers. Follow Tayyab Electric Studio on Facebook!
            </p>
            <a
              href="https://www.facebook.com/tayyabelectricstudio"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-[#1877F2] hover:bg-[#1565C0] text-white px-6 py-3 rounded-full font-semibold transition-all duration-300"
            >
              <span className="font-black text-lg leading-none">f</span>
              <span>Follow on Facebook</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── Embed Modal ── */}
      <AnimatePresence>
        {activePost && (
          <EmbedModal post={activePost} onClose={() => setActivePost(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
